<template>
  <LoginLayout :image="getImageUrl()" :is-page="true">
    <div
      v-if="!successProcess"
      class="mx-auto flex h-full max-w-md flex-col gap-3 px-6 lg:my-24 lg:px-0"
    >
      <h2 class="mb-8 text-2xl font-bold">Change Your Password</h2>
      <p class="text-base lg:mb-3">
        For your security, we highly recommend that you choose a unique password
        that you don’t use for any other online account.<br /><br />
        Use only letters, numbers, and common symbols. Avoid spaces at the start
        and end.
      </p>

      <ClientOnly>
        <VeeValidateForm v-slot="{ meta }" novalidate @submit="submit">
          <FormInput
            v-model="form.new_password"
            type="password"
            name="new_password"
            label="Enter New Password"
            help-text="*Must be at least 8 characters"
            rules="password"
            label-classes="!text-sm mb-2"
            autocomplete="new-password"
            :use-field-validation-color="true"
            :passwords-do-not-match="passwordsDoNotMatchFieldOne"
          />

          <FormInput
            v-model="form.new_password_confirmation"
            type="password"
            name="new_password_confirmation"
            label="Re-enter Password"
            help-text="*Both passwords must match"
            rules="password"
            label-classes="!text-sm mb-2"
            autocomplete="new-password"
            :use-field-validation-color="true"
            :passwords-do-not-match="passwordsDoNotMatchFieldTwo"
          />
          <PasswordStrength
            :input-value="form.new_password"
            :confirmation-value="form.new_password_confirmation"
            @on-valid="isPasswordValid = $event"
          />

          <div v-if="form.errors.get('message')" class="my-1 text-error">
            {{ form.errors.get('message') }}
          </div>

          <div class="mt-9 md:text-right">
            <Button
              class="w-full md:will-change-auto"
              :class="!isPasswordValid && 'cursor-not-allowed'"
              variant="primary"
              type="submit"
              :disabled="form.busy || !meta.valid || !isPasswordValid"
              :show-spinner="form.busy"
            >
              Change Password
            </Button>
          </div>
        </VeeValidateForm>
      </ClientOnly>
    </div>
    <div
      v-else
      class="mx-auto flex max-w-md flex-col items-center gap-3 px-6 lg:px-0"
    >
      <ClientOnly>
        <h2 class="mb-8 text-2xl font-bold">Your password has been reset.</h2>
        <Button
          class="w-full border-2 border-p-500 !text-sm lg:w-[188px]"
          variant="primary"
          @click="$router.push('/login')"
        >
          {{ SING_IN_TEXT }}
        </Button>
        <Button
          class="w-full border-2 border-p-500 bg-white !text-sm text-black hover:border-p-500 hover:bg-p-500 hover:text-black lg:w-auto"
          variant="secondary"
          @click="redirectToHome"
        >
          {{ RETURN_TO_HOMEPAGE_TEXT }}
        </Button>
      </ClientOnly>
    </div>
  </LoginLayout>
</template>

<script setup lang="ts">
/** packages */
import { Form as VeeValidateForm } from 'vee-validate'

/** components */
import Button from '~/components/common/Button.vue'
import FormInput from '~/components/common/FormInput.vue'
import LoginLayout from '~/components/common/LoginLayout.vue'
import PasswordStrength from '~/components/PasswordStrength.vue'

/** composables */
import { useSeo } from '~/composables/useSeo'
import { useEventBus } from '~/composables/useEventBus'
import { useScreenSize } from '~/composables/useScreenSize'

/** utils */
import { Form } from '~/utils/form'

/** types */
import type { TFormSetPassword } from '~/src/types/form'
import type { THttpClientResponse } from '~/src/types/http'

/** constants */
import {
  MANAGE_PASSWORD_IMAGES,
  MESSAGE_SUCCESS_SET_PASSWORD,
} from '~/src/constants/auth'
import { IMAGE_SIZES } from '~/src/constants/common'
import { RETURN_TO_HOMEPAGE_TEXT, SING_IN_TEXT } from '~/src/constants/forms'

/** app */
const { $restClient } = useNuxtApp()
const $router = useRouter()
const $route = useRoute()
const { isNotDesktop } = useScreenSize()
const { getSeoInfo } = useSeo()
const { emit, on } = useEventBus()
const seo = getSeoInfo({ title: 'Set your new password' })

/** refs */
const passwordsDoNotMatchFieldOne = ref<boolean | null>(null)
const passwordsDoNotMatchFieldTwo = ref<boolean | null>(null)
const successProcess = ref<boolean>(false)
const isPasswordValid = ref<boolean>(false)
const propertiesForm = {
  new_password: '',
  new_password_confirmation: '',
  email: '',
  token: '',
} as TFormSetPassword
const form = ref(new Form<TFormSetPassword>(null, propertiesForm))

/** computed */
const imageType = computed(() => {
  return isNotDesktop.value ? IMAGE_SIZES.MOBILE : IMAGE_SIZES.DESKTOP
})

/** methods */
const getImageUrl = () => MANAGE_PASSWORD_IMAGES[imageType.value]

const loadParams = () => {
  form.value.email = $route.query.email
  form.value.token = $route.query.token
}

const submit = async (_values, { resetForm }) => {
  form.value.startProcessing()

  try {
    const response = await $restClient.user.setPassword({
      email: form.value.email,
      new_password: form.value.new_password,
      token: form.value.token,
    })
    showException(response?.message)
    successProcess.value = true
    form.value.finishProcessing()
    loadParams()
  } catch (error) {
    form.value.setErrors({ message: error?.message ?? error })
  } finally {
    form.value.reset()
    resetForm()
    emit('resetPasswordCriteria', true)
  }
}

const showException = (message: THttpClientResponse) => {
  if (message?.toLowerCase() !== MESSAGE_SUCCESS_SET_PASSWORD) {
    throw new Error(message)
  }
}

const redirectToHome = () => {
  window.location.href = '/'
}

/** lifecycle */
useHead(seo)
onMounted(() => {
  loadParams()
  on('passwordsMatch', (value) => {
    if (value.field === 'new_password' && value.match !== null) {
      if (value.match) {
        passwordsDoNotMatchFieldTwo.value = false
      }
      passwordsDoNotMatchFieldOne.value = !value.match
    }
    if (value.field === 'new_password_confirmation' && value.match !== null) {
      if (value.match) {
        passwordsDoNotMatchFieldOne.value = false
      }
      passwordsDoNotMatchFieldTwo.value = !value.match
    }
  })
})
definePageMeta({ layout: 'empty' })
</script>
